import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const content = {
  sv: {
    title: "Varför välja Diamond Domes?",
    subtitle: "Helhetslösningar med fokus på kvalitet och service",
    reasons: [
      {
        title: "Helhetslösningar",
        description: "Från idé till färdig kupol — personlig rådgivning genom hela processen."
      },
      {
        title: "Svenska marknaden",
        description: "Vi förstår nordiska krav. Alla kupoler är anpassade för vårt klimat."
      },
      {
        title: "Året runt",
        description: "Konstruerade för optimal funktion i alla årstider."
      },
      {
        title: "Design & funktion",
        description: "Estetik kombinerat med praktisk prestanda."
      },
      {
        title: "Gedigna garantier",
        description: "Långsiktiga garantier och pålitlig kundservice."
      }
    ],
    cta: "Kontakta oss för en offert"
  },
  en: {
    title: "Why Choose Diamond Domes?",
    subtitle: "Complete solutions with focus on quality and service",
    reasons: [
      {
        title: "Complete Solutions",
        description: "From idea to finished dome — personal guidance throughout the process."
      },
      {
        title: "Swedish Market",
        description: "We understand Nordic requirements. All domes are adapted for our climate."
      },
      {
        title: "Year-Round",
        description: "Designed for optimal function in all seasons."
      },
      {
        title: "Design & Function",
        description: "Aesthetics combined with practical performance."
      },
      {
        title: "Solid Guarantees",
        description: "Long-term guarantees and reliable customer service."
      }
    ],
    cta: "Contact us for a quote"
  },
  de: {
    title: "Warum Diamond Domes wählen?",
    subtitle: "Komplettlösungen mit Fokus auf Qualität und Service",
    reasons: [
      {
        title: "Komplettlösungen",
        description: "Von der Idee bis zur fertigen Kuppel — persönliche Beratung im gesamten Prozess."
      },
      {
        title: "Schwedischer Markt",
        description: "Wir verstehen nordische Anforderungen. Alle Kuppeln sind an unser Klima angepasst."
      },
      {
        title: "Ganzjährig",
        description: "Konstruiert für optimale Funktion in allen Jahreszeiten."
      },
      {
        title: "Design & Funktion",
        description: "Ästhetik kombiniert mit praktischer Leistung."
      },
      {
        title: "Solide Garantien",
        description: "Langfristige Garantien und zuverlässiger Kundenservice."
      }
    ],
    cta: "Kontaktieren Sie uns für ein Angebot"
  }
};

export const WhyDiamondDomes = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <section id="why-diamond-domes" className="py-20 px-4 md:px-6 gradient-soft">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 animate-slide-up">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-foreground">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {t.reasons.map((reason, index) => (
            <div 
              key={index}
              className="flex gap-4 p-6 bg-background rounded-xl shadow-sm animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  {reason.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {reason.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center animate-slide-up">
          <Button asChild size="lg" className="gradient-hero text-white hover:opacity-90 transition-smooth shadow-elegant group">
            <Link to="/contact">
              {t.cta}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-smooth" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
